import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { queryClient, queryKeys } from './query-client';

// Client data layer for the empty-chat suggested-task chips. One fetch to /api/suggested-tasks (the
// symphony-bff ListSuggestedTasks projection of the genome's `_suggested-tasks.json`). orion-web's
// CLIENT bundle must not import common-lib, so the shape lives here as a client-local contract
// matching what the route returns.

export interface SuggestedTask {
  /** The chip's visible text. Short and imperative; already localized by the genome. */
  label: string;
  /** What to put in the COMPOSER on tap. The user still presses send — never auto-send this. */
  prompt: string;
  /** Why this business specifically. Shown on hover; empty means show no tooltip. */
  why: string;
  /** Composio toolkit slug for the chip's logo, or '' when the task rides no particular app. */
  connector: string;
  /** True when `connector` is NOT connected yet — the chip must say so before it is tapped. */
  requiresConnect: boolean;
}

export type SuggestedTasksState =
  | { status: 'loading' }
  | { status: 'ready'; tasks: SuggestedTask[] }
  /** Nothing to show. Deliberately distinct from 'ready' with an empty list only in intent: both
   *  render nothing, and neither is worth an error message under a working composer. */
  | { status: 'empty' };

export interface SuggestedTasksResponse {
  tasks: SuggestedTask[];
  /** How many setup questions the genome still has for the owner. Above zero shows the "Complete your setup" control. */
  openSetupQuestions: number;
}

export async function fetchSuggestedTasks(): Promise<SuggestedTasksResponse> {
  const res = await apiFetch(`${API_BASE}/api/suggested-tasks`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return parseSuggestedTasksResponse(await res.json());
}

export function parseSuggestedTasksResponse(json: unknown): SuggestedTasksResponse {
  const data = (json ?? {}) as { tasks?: Partial<SuggestedTask>[]; openSetupQuestions?: unknown };
  const open = typeof data.openSetupQuestions === 'number' && Number.isFinite(data.openSetupQuestions) ? Math.max(0, Math.floor(data.openSetupQuestions)) : 0;
  const tasks = (data.tasks ?? [])
    .map((t) => ({
      label: t.label ?? '',
      // A chip whose tap puts nothing in the composer is a dead control.
      prompt: t.prompt || t.label || '',
      why: t.why ?? '',
      connector: t.connector ?? '',
      requiresConnect: Boolean(t.requiresConnect),
    }))
    .filter((t) => t.label.length > 0);
  return { tasks, openSetupQuestions: open };
}

/** How many chips the row shows at once — the genome can author more than fit the composer. */
export const SUGGESTED_TASK_CHIP_COUNT = 3;

/**
 * `count` tasks picked at random out of the full authored list, in random order. Pure (takes the
 * randomness source as a parameter) so it is exercised without mocking `Math.random`. A list no
 * longer than `count` is returned as-is — there's nothing to sample down.
 */
export function pickRandomSuggestedTasks(tasks: SuggestedTask[], count = SUGGESTED_TASK_CHIP_COUNT, random: () => number = Math.random): SuggestedTask[] {
  if (tasks.length <= count) return tasks;
  // Fisher–Yates, stopped after `count` swaps — no need to shuffle the tail we're about to drop.
  const pool = [...tasks];
  for (let i = 0; i < count; i++) {
    const j = i + Math.floor(random() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

/**
 * The chips for an empty chat.
 *
 * A failure here is NOT an error state: the composer under these chips is the actual feature, and a
 * red line where three optional buttons would be is worse than the buttons never appearing. So every
 * non-ready outcome — still loading, failed, none authored — resolves to a row that draws nothing.
 * The duplexer refresh matters because the genome rewrites this file on every recalc, and a user who
 * just finished onboarding should see their first chips without a reload.
 *
 * The genome can author more suggestions than the row should show at once, so only
 * `SUGGESTED_TASK_CHIP_COUNT` of them render — a random sample, re-picked whenever the underlying list
 * changes (a fresh fetch, a genome recalc via the duplexer), so the same handful doesn't calcify as
 * "the chips". The pick is memoized on the query's own data reference, not re-rolled on every render,
 * so it stays put while the chips are actually on screen (typing in the composer must not shuffle them
 * out from under a reader mid-decision).
 */
export function useSuggestedTasks(enabled: boolean): SuggestedTasksState {
  const query = useQuery({
    queryKey: queryKeys.suggestedTasks,
    queryFn: fetchSuggestedTasks,
    enabled,
  });

  useFileSystemDuplexer(() => {
    if (enabled) void queryClient.invalidateQueries({ queryKey: queryKeys.suggestedTasks });
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the data array's own identity
  const shown = useMemo(() => (query.data ? pickRandomSuggestedTasks(query.data.tasks) : undefined), [query.data]);

  if (shown) return shown.length > 0 ? { status: 'ready', tasks: shown } : { status: 'empty' };
  if (query.status === 'error') return { status: 'empty' };
  return enabled ? { status: 'loading' } : { status: 'empty' };
}

/**
 * How many setup questions are still open, for the "Complete your setup" header control. Rides the
 * same query as the chips, so a header and the chat's chip row share one fetch. Zero while loading
 * and on failure: the control simply doesn't show.
 *
 * No duplexer subscription here, unlike the chips: a header is on screen most of the time, and the
 * file-system channel fires on every file the agent writes, so it would re-read three files per write
 * all through a run. The questions only change on a genome recalc, which the next mount or window
 * focus picks up.
 */
export function useOpenSetupQuestions(): number {
  const query = useQuery({ queryKey: queryKeys.suggestedTasks, queryFn: fetchSuggestedTasks });
  return query.data?.openSetupQuestions ?? 0;
}
